import convertPersonaToRecipientViewState from './convertPersonaToRecipientViewState';
import FindControlViewState from 'owa-recipient-types/lib/types/FindControlViewState';
import findReadWriteRecipient from 'owa-readwrite-recipient-well/lib/actions/findReadWriteRecipient';
import parseOneOffEmailAddressFromString from 'owa-recipient-email-address/lib/utils/parseOneOffEmailAddressFromString';
import dangerousCreateSMTPEmailAddressWithoutValidation from 'owa-recipient-email-address/lib/utils/dangerousCreateSMTPEmailAddressWithoutValidation';
import getDisplayNameAndAddressFromRecipientString from 'owa-recipient-email-address/lib/utils/getDisplayNameAndAddressFromRecipientString';
import ReadWriteRecipientViewState from 'owa-recipient-types/lib/types/ReadWriteRecipientViewState';
import updateQueryString from '../actions/updateQueryString';
import createFindControlViewState from 'owa-recipient-common/lib/utils/createFindControlViewState';
import { splitPasteInputIntoRecipientStrings } from './splitPasteInputIntoRecipientStrings';

export default async function parseTextForRecipient(
    recipientWell: FindControlViewState,
    text: string,
    strict: boolean
): Promise<ReadWriteRecipientViewState> {
    let trimmedText = text.trim();
    if (trimmedText.length <= 0) {
        return null;
    }

    // try to parse out a suspected name + email address combo
    let nameAndAddress = getDisplayNameAndAddressFromRecipientString(trimmedText);
    let isResolved = false;

    // Parse the recipient from the address string
    const parsedRecipientEmail = parseOneOffEmailAddressFromString(
        nameAndAddress.address,
        nameAndAddress.displayName
    );

    // If resolution is non-strict or the address is valid,
    // use the first result from findReadWriteRecipient.
    if (!strict && parsedRecipientEmail) {
        await findReadWriteRecipient(
            recipientWell,
            nameAndAddress.address,
            true /* searchDirectory */
        );

        // If we have a single recipient from findReadWriteRecipient that
        // matches the exact address we have already, use it.
        if (
            recipientWell.findResultSet.length === 1 &&
            recipientWell.findResultSet[0].EmailAddress.EmailAddress == nameAndAddress.address
        ) {
            return convertPersonaToRecipientViewState(
                [recipientWell.findResultSet[0]],
                !isResolved /*validationNeeded*/
            )[0];
        }
    }

    // Finally, if we can neither parse the address nor find a real readWriteRecipient,
    // we just lie and say the input text was an SMTP.
    const parsedOrFakedRecipientEmail =
        parsedRecipientEmail ||
        dangerousCreateSMTPEmailAddressWithoutValidation(
            nameAndAddress.address,
            nameAndAddress.displayName
        );

    const parsedOrFakedRecipient = {
        // TODO (#56102) should we be setting/propagating isValid here?
        // the old implementation didn't ever set it, and personas that haven't been
        // validated propagate around a lot.
        isValid: false,
        EmailAddress: parsedOrFakedRecipientEmail,
    };

    return convertPersonaToRecipientViewState(
        [parsedOrFakedRecipient],
        !isResolved /*validationNeeded*/
    )[0];
}

export async function parsePastedTextForRecipients(
    recipientWell: FindControlViewState,
    pastedText: string
): Promise<ReadWriteRecipientViewState[]> {
    let recipientParsingPromises = [];
    let recipientList = splitPasteInputIntoRecipientStrings(pastedText);
    for (let recipientText of recipientList) {
        updateQueryString(recipientWell, recipientText);
        recipientParsingPromises.push(
            parseTextForRecipient(
                createFindControlViewState(recipientText),
                recipientText,
                false /*strict*/
            )
        );
    }

    return await Promise.all(recipientParsingPromises);
}
